/**
 * Paisley landmark image pool — used as a rotation fallback for any card
 * where a specific featured image isn't available (e.g. the Trending widget,
 * category tiles, share previews, etc.).
 *
 * All 5 images are hero-quality Paisley landmark shots supplied by the editor
 * (Abbey aerial, Russell Institute, snowy Abbey/river, Town Hall aerial,
 * Coats Observatory + Anchor Mill skyline). Each carries the #PositivePaisley
 * / paisley.org.uk watermark, so they double as a subtle brand promotion.
 *
 * Rotation is DETERMINISTIC on a seed (post id or slug) — same seed always
 * maps to the same landmark, so a card doesn't flip images on every render
 * and cached CDN copies stay useful.
 */

export const PAISLEY_LANDMARKS = [
  {
    url: "https://customer-assets.emergentagent.com/job_0f92823c-363c-4bef-8518-60de4aa23481/artifacts/byem2pu3_C4A7E44A-8DF0-4162-B770-BADC6E161405.PNG",
    alt: "Aerial view of Paisley Abbey, Town Hall and town centre",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_0f92823c-363c-4bef-8518-60de4aa23481/artifacts/a3hjbk0w_63B8FF49-59AF-4C85-BF01-83333FC0C0F3.PNG",
    alt: "The Russell Institute — Art Deco landmark on Causeyside Street, Paisley",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_0f92823c-363c-4bef-8518-60de4aa23481/artifacts/e6iyeebo_9D2CA1E3-9ACB-4B3E-8A9E-F7B9A7BF8D06.PNG",
    alt: "Snow-covered Paisley Abbey and Town Hall beside the White Cart Water",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_0f92823c-363c-4bef-8518-60de4aa23481/artifacts/qlhhdd3b_9EA960B6-A42B-4CC5-BB30-11A1230D86EA.PNG",
    alt: "Aerial view of Paisley Town Hall over the White Cart Water",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_0f92823c-363c-4bef-8518-60de4aa23481/artifacts/gce9ncwt_add-a-small-subtle-watermark-text-paisleyorguk-in-.jpeg",
    alt: "Coats Observatory clock tower with Anchor Mill on the Paisley skyline",
  },
];

/**
 * djb2-style deterministic hash — fast, no dependencies, stable across
 * renders and browsers. Enough entropy to spread ~thousands of slugs
 * evenly across the 5 landmarks.
 */
const _hashSeed = (seed) => {
  const s = String(seed || "");
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

/**
 * Returns a landmark image (URL + alt) for the given seed. Same seed →
 * same landmark, so a card doesn't shuffle on every render.
 */
export const getLandmarkImage = (seed = "") => {
  const idx = _hashSeed(seed) % PAISLEY_LANDMARKS.length;
  return PAISLEY_LANDMARKS[idx];
};

/**
 * Returns a landmark URL only (convenience wrapper for cases where the alt
 * text can be derived from the calling context — e.g. the post title).
 */
export const getLandmarkUrl = (seed = "") => getLandmarkImage(seed).url;
